import { createContext } from "preact";
import { useContext } from "preact/hooks";
import { useSignal } from "@preact/signals";
import "@/app/copy-formatter.ts";
import { Layout } from "@/components/Layout.tsx";
import type { Chat, ExamplePrompt, Message, Mode, User, Site, Doc } from "db";
import { ChatIndex } from "./History.tsx";
import "preact/debug";

export interface AppProps {
  user: User | null;
  chatData: Chat | null;
  siteData: Site | null;
  /**
   * Used to determine if we are on the start page (no chat), or we are to load the chat.
   */
  isStartPage: boolean;
  /**
   * If we are setting the user password 
   */
  isPasswordSet: boolean;
  modes: Mode[];
  history: ChatIndex[];
  examplePrompts: ExamplePrompt[];
  siteURL: string;
  path: string;
}

export default function App({ data }: { data: AppProps }) {
  const appData = new AppData(data)
  return (
    <div class="app-ui">
      <AppDataContext.Provider value={appData}> <Layout /> </AppDataContext.Provider>
    </div>
  );
}

class AppData {
  messages = useSignal<Message[]>([]);
  user = useSignal<User | null>(null);
  chatData = useSignal<Chat | null>(null);
  siteData = useSignal<Site | null>(null);
  documents = useSignal<Doc[]>([]);
  modes = useSignal<Mode[]>([]);
  history = useSignal<ChatIndex[]>([]);
  examplePrompts = useSignal<ExamplePrompt[]>([]);
  isStartPage = useSignal<boolean>(true);
  isPasswordSet = useSignal<boolean>(false);
  siteURL = "";
  generating = useSignal(false);
  path: string;

  constructor(props: AppProps) {
    this.user.value = props.user;
    this.chatData.value = props.chatData;
    this.isStartPage.value = props.isStartPage;
    this.siteData.value = props.siteData;
    this.modes.value = props.modes;
    this.history.value = props.history;
    this.examplePrompts.value = props.examplePrompts;
    this.isPasswordSet.value = props.isPasswordSet;
    this.siteURL = props.siteURL;
    this.path = props.path;
  }
}

const AppDataContext = createContext<AppData | null>(null);

export const useAppData = () => useContext(AppDataContext) as AppData;